import { CheckedAddress } from '@sparkdotfi/common-universal'
import { useCallback, useMemo, useRef } from 'react'
import { TransactionReceipt, zeroAddress } from 'viem'
import { useChainId, useConfig } from 'wagmi'

import { createPermitStore } from './permits'
import { Action, ActionContext, InjectedActionsContext } from './types'
import { useIntegratorRefCode } from './useIntegratorRefCode'
import { useTokenApprovals } from './useTokenApprovals'
import { useSandbox } from '@/domain/sandbox/useSandbox'
import { useActionsSettings } from '@/domain/state'
import { useConnectedAddress } from '@/domain/wallet/useConnectedAddress'

export interface UseActionsContextResult {
  context: ActionContext
  resetContext: () => void
}

export function useActionsContext(injectedContext?: InjectedActionsContext): UseActionsContextResult {
  const permitStore = useMemo(() => createPermitStore(), []) // useMemo not to call createPermitStore on every render
  const txReceiptsRef = useRef<[Action, TransactionReceipt][]>([])
  const chainId = useChainId()
  const { account = CheckedAddress(zeroAddress) } = useConnectedAddress({ throwOnNotConnected: false }) // stub to have a valid address
  const wagmiConfig = useConfig()
  const { isInSandbox } = useSandbox()
  const tokenApprovals = useTokenApprovals()
  const actionSettings = useActionsSettings()
  const integratorRefCode = useIntegratorRefCode()

  const resetContext = useCallback(() => {
    permitStore.clear()
    txReceiptsRef.current = []
  }, [permitStore])

  return {
    context: {
      permitStore,
      txReceipts: txReceiptsRef.current,
      wagmiConfig,
      account,
      chainId,
      integratorRefCode,
      isInSandbox,
      tokenApprovals,
      actionSettings,
      ...injectedContext,
    },
    resetContext,
  }
}
